import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CheckOutlined, CloseOutlined, DoubleRightOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Input, Switch, Table } from 'antd';
import { axiosErrorHandler, conditionFilter, lowerCaseIncludes } from '../../../../../swzutils';
import DriverFatigueIcon from '../DriverFatigueIcon';
import DriverStatusIcon from '../markers/DriverStatusIcon';
import { isUserMultiTenant, userHasContractTenancy, userHasPermission } from "../../../../../swzutils/UserPermissionsUtil";
import HelpLabel from "../../../components/HelpLabel";
import VehicleTypeCategorySelect from "../../../components/VehicleTypeCategorySelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationArrow } from '@fortawesome/free-solid-svg-icons/faLocationArrow';
import HasPermissions from "../../../../../containers/HasPermissions";
import { VehicleTypeCategoryEnum } from "../../vehicleTypes/api/Representation";
const paginationConfig = {
    /*
     https://ant.design/components/pagination/

     total: number;
     defaultCurrent?: number;
     current?: number;
     defaultPageSize?: number;
     pageSize?: number;
     onChange?: (page: number, pageSize: number) => void;
     showSizeChanger?: boolean;
     pageSizeOptions?: string[];
     onShowSizeChange?: (current: number, size: number) => void;
     showQuickJumper?: boolean;
     showTotal?: (total: number) => React.ReactNode;
     size?: string;
     simple?: boolean;
     style?: React.CSSProperties;
     locale?: Object;
     className?: string;
     prefixCls?: string;
     selectPrefixCls?: string;
    "pageSizeOptions": ["10", "25", "50"]
     */
    "showTotal": (total, range) => `${range[0]}-${range[1]} of ${total}`,
    "size": "small", // size of buttons - can be medium or small
    "showQuickJumper": false, // Shows a GoTo text field in pagination to quickly go to a page
    "showSizeChanger": false,
    "defaultPageSize": 100,
};
const FilterPanel = ({ loggedInUser, selectedTenant, driverLocations, selectedDriverLocation, showSpinner, searchText, onClose, onDriverClicked, onSearch, onRefreshClicked, filterEmployees, onFilterEmployeesChanged, filterContractors, onFilterContractorsChanged, filterCarriers, onFilterCarriersChanged, filterIncludeMarketplace, onFilterIncludeMarketplaceChanged, filterVehicleCategories, onFilterVehicleCategoriesChanged }) => {
    const [vehicles, setVehicles] = useState([]);
    const isRowSelected = (record) => {
        return selectedDriverLocation
            && record.location
            && selectedDriverLocation.id === record.location.id;
    };
    const loadVehicles = () => {
        if (userHasPermission(loggedInUser, ['admin:vehicle:list'])) {
            axios.get('/admin/v1/vehicles?limit=999&active=true')
                .then(response => setVehicles(response.data.results))
                .catch(axiosErrorHandler);
        }
    };
    useEffect(() => {
        if (loggedInUser && (!isUserMultiTenant(loggedInUser) || selectedTenant?.id)) {
            loadVehicles();
        }
    }, [loggedInUser, selectedTenant?.id]);
    const buildTableRecords = () => {
        let tableData = [];
        const regoSet = new Set();
        driverLocations
            .filter((dl) => dl.vehicle && dl.driverSummary)
            .forEach((dl) => {
            regoSet.add(dl.vehicle.registration);
            const surname = dl.driverSummary.lastName ? dl.driverSummary.lastName.substring(0, 1) : "";
            tableData.push({
                id: "DL-" + dl.id,
                driverName: dl.driverSummary.firstName + " " + surname,
                rego: dl.vehicle.registration,
                vehicleId: dl.vehicle.id,
                location: dl
            });
        });
        vehicles
            .filter((v) => !regoSet.has(v.registration))
            .forEach((v) => {
            if (searchText && !lowerCaseIncludes(v.registration, searchText)) {
                return;
            }
            tableData.push({
                id: "V" + v.id,
                driverName: "- None -",
                rego: v.registration
            });
        });
        return tableData;
    };
    const tableRecords = useMemo(() => buildTableRecords(), [driverLocations, vehicles]);
    const buildTableColumns = () => {
        const columns = [{
                title: (_jsx(HelpLabel, { title: "Driver", helpText: "By clicking on the driver name it will take you directly to the vehicle on the map, here you will see real time information of the vehicle and any started deliveries at a glance. Drivers will appear in the list only when they are logged with a vehicle registration." })),
                sorter: false,
                dataIndex: "driverName",
                condition: true,
                render: (text, record) => {
                    const fatigueBreaches = record.location && record.location.fatigueBreaches;
                    return (_jsxs("span", { children: [_jsx(DriverFatigueIcon, { fatigueBreaches: fatigueBreaches }), _jsx(DriverStatusIcon, { driverLocation: record.location }), text] }));
                }
            }, {
                title: 'Rego',
                dataIndex: "rego",
                sorter: false,
                condition: true
            }, {
                title: (_jsx(HelpLabel, { title: "Map", helpText: "By the selecting the map icon it will take you directly to a Route timeline, here you will see Real time Eta and Completed times, turn by turn tracking and pick up and drop off locations at a glance." })),
                sorter: false,
                condition: userHasPermission(loggedInUser, ['admin:vehicle:deliveries']),
                render: (text, record) => {
                    if (!record.vehicleId) {
                        return;
                    }
                    return (_jsx("a", { className: "btn btn-icon-white", href: `#/app/map/vehicle-route/${record.vehicleId}?settingsTenantId=${selectedTenant?.id}`, children: _jsx(FontAwesomeIcon, { icon: faLocationArrow }) }));
                }
            }];
        return columns.filter(conditionFilter);
    };
    const tableColumns = useMemo(() => buildTableColumns(), [loggedInUser]);
    const isLoggedInUserMultiTenant = isUserMultiTenant(loggedInUser);
    const nonMultiTenantOrHasTenantSelected = (!isLoggedInUserMultiTenant || (isLoggedInUserMultiTenant && selectedTenant?.id));
    const canViewFleetVehicles = nonMultiTenantOrHasTenantSelected && userHasPermission(loggedInUser, ['admin:vehicle:list']);
    const canViewPartnerVehicles = userHasPermission(loggedInUser, ['admin:tenant:partnerships:listVehicles']);
    const canSeeMarketplaceJobs = (userHasContractTenancy(loggedInUser, selectedTenant)
        && userHasPermission(loggedInUser, ['admin:consignment:listMarketplace', 'admin:vehicleHires:listMarketplace']));
    const showFilterOptions = canViewFleetVehicles || canViewPartnerVehicles || canSeeMarketplaceJobs;
    return (_jsxs("div", { className: "map-search", children: [_jsxs("h2", { className: "article-title article-title-sm", children: ["Filter", _jsx(Button, { type: "primary", className: "pull-right", icon: _jsx(DoubleRightOutlined, {}), onClick: onClose })] }), showFilterOptions &&
                _jsxs("div", { children: [canViewFleetVehicles &&
                            _jsxs("div", { style: { paddingTop: '10px' }, children: [_jsx(Switch, { checked: filterEmployees, checkedChildren: _jsx(CheckOutlined, {}), unCheckedChildren: _jsx(CloseOutlined, {}), onClick: onFilterEmployeesChanged }), "\u00A0My Drivers"] }), canViewPartnerVehicles &&
                            _jsxs("div", { style: { paddingTop: '10px' }, children: [_jsx(Switch, { checked: filterContractors, checkedChildren: _jsx(CheckOutlined, {}), unCheckedChildren: _jsx(CloseOutlined, {}), onClick: onFilterContractorsChanged }), "\u00A0Contractor Partners"] }), canViewPartnerVehicles && nonMultiTenantOrHasTenantSelected &&
                            _jsxs("div", { style: { paddingTop: '10px' }, children: [_jsx(Switch, { checked: filterCarriers, checkedChildren: _jsx(CheckOutlined, {}), unCheckedChildren: _jsx(CloseOutlined, {}), onClick: onFilterCarriersChanged }), "\u00A0Carrier Partners"] }), canSeeMarketplaceJobs &&
                            _jsxs("div", { style: { paddingTop: '10px' }, children: [_jsx(Switch, { checked: filterIncludeMarketplace, checkedChildren: _jsx(CheckOutlined, {}), unCheckedChildren: _jsx(CloseOutlined, {}), onClick: onFilterIncludeMarketplaceChanged }), "\u00A0Marketplace Jobs"] }), _jsx(HasPermissions, { permissions: ['admin:vehicleTypeCategory:listAll', 'admin:vehicleTypeCategory:listMarketplace'], children: _jsxs("div", { style: { paddingTop: '10px' }, children: [_jsx("span", { children: "Vehicle Categories" }), _jsx("br", {}), _jsx(VehicleTypeCategorySelect, { defaultValue: filterVehicleCategories, style: { width: "100%" }, mode: "multiple", placeholder: "Vehicle Categories", onChange: onFilterVehicleCategoriesChanged })] }) })] }), _jsxs("div", { style: { paddingTop: '10px' }, children: [_jsx(Input.Search, { placeholder: "Driver name or Rego", onSearch: onSearch }), _jsx(Button, { type: "primary", className: "refresh-btn pull-right", loading: showSpinner, icon: _jsx(ReloadOutlined, {}), onClick: onRefreshClicked })] }), _jsx(Table, { style: { paddingTop: "15px" }, className: "filter-search-table", columns: tableColumns, size: "middle", rowKey: record => record.id, rowClassName: (record /*, index */) => { return isRowSelected(record) ? "selected thin" : "thin"; }, dataSource: tableRecords, pagination: paginationConfig, loading: showSpinner, onRow: (record) => {
                    return {
                        onClick: () => { record.location && onDriverClicked(record.location); }
                    };
                } })] }));
};
export default FilterPanel;
